import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Droplets, Package, ShoppingCart, FileText, Plus, Minus,
  IndianRupee, TrendingUp, Archive, Printer, Trash2, Edit,
  AlertTriangle, Calendar
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import BillTemplate from '../components/BillTemplate';

const Lubricants = () => {
  const { token, user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billSettings, setBillSettings] = useState(null);
  const [pumpProfile, setPumpProfile] = useState(null);
  
  // Dialogs
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showRestockDialog, setShowRestockDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [restockProduct, setRestockProduct] = useState(null);
  
  // Product form
  const [productForm, setProductForm] = useState({
    name: '', brand: '', category: 'Engine Oil', pack_size_ml: '',
    cost_price: '', sell_price: '', gst_rate: '18', min_stock_alert: '5', sku: '',
    initial_stock: '', expiry_date: ''
  });
  
  // Restock form (simplified purchase)
  const [restockForm, setRestockForm] = useState({
    quantity: '', cost_price_per_unit: '', expiry_date: '', supplier_name: ''
  });
  
  // Sale form
  const [saleForm, setSaleForm] = useState({
    date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    customer_name: '', customer_mobile: '', customer_vehicle: '',
    items: [], payment_method: 'cash', notes: ''
  });
  const [saleItems, setSaleItems] = useState([{ product_id: '', quantity: 1 }]);

  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [settingsRes, productsRes, salesRes, purchasesRes, dashboardRes, billRes] = await Promise.all([
        fetch(`${API}/lubricants/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/lubricants/products`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/lubricants/sales`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/lubricants/purchases`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/lubricants/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/bill-settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
      if (salesRes.ok) {
        const data = await salesRes.json();
        setSales(data.sales || []);
      }
      if (purchasesRes.ok) {
        const data = await purchasesRes.json();
        setPurchases(data.purchases || []);
      }
      if (dashboardRes.ok) setDashboardStats(await dashboardRes.json());
      if (billRes.ok) {
        const billData = await billRes.json();
        setBillSettings(billData.bill_settings);
        setPumpProfile(billData.pump_profile);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Product handlers
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProduct 
        ? `${API}/lubricants/products/${editingProduct.id}`
        : `${API}/lubricants/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      
      const payload = {
        ...productForm,
        pack_size_ml: parseInt(productForm.pack_size_ml),
        cost_price: parseFloat(productForm.cost_price),
        sell_price: parseFloat(productForm.sell_price),
        gst_rate: parseFloat(productForm.gst_rate),
        min_stock_alert: parseInt(productForm.min_stock_alert)
      };
      
      // Add initial stock only for new products
      if (!editingProduct && productForm.initial_stock) {
        payload.initial_stock = parseInt(productForm.initial_stock);
      }
      if (!editingProduct && productForm.expiry_date) {
        payload.expiry_date = productForm.expiry_date;
      }
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(editingProduct ? 'Product updated' : 'Product added');
        setShowProductDialog(false);
        setEditingProduct(null);
        resetProductForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', brand: '', category: 'Engine Oil', pack_size_ml: '',
      cost_price: '', sell_price: '', gst_rate: '18', min_stock_alert: '5', sku: '',
      initial_stock: '', expiry_date: ''
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      brand: product.brand,
      category: product.category,
      pack_size_ml: product.pack_size_ml.toString(),
      cost_price: product.cost_price.toString(),
      sell_price: product.sell_price.toString(),
      gst_rate: product.gst_rate.toString(),
      min_stock_alert: product.min_stock_alert.toString(),
      sku: product.sku || ''
    });
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const response = await fetch(`${API}/lubricants/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Product deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Restock handler (simplified purchase)
  const handleRestockSubmit = async (e) => {
    e.preventDefault();
    if (!restockProduct) return;
    
    try {
      const response = await fetch(`${API}/lubricants/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_id: restockProduct.id,
          date: new Date().toISOString(),
          quantity: parseInt(restockForm.quantity),
          cost_price_per_unit: parseFloat(restockForm.cost_price_per_unit || restockProduct.cost_price),
          expiry_date: restockForm.expiry_date ? new Date(restockForm.expiry_date).toISOString() : null,
          supplier_name: restockForm.supplier_name || null
        })
      });
      
      if (response.ok) {
        toast.success(`Added ${restockForm.quantity} units to ${restockProduct.name}`);
        setShowRestockDialog(false);
        setRestockProduct(null);
        setRestockForm({ quantity: '', cost_price_per_unit: '', expiry_date: '', supplier_name: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to restock');
      }
    } catch (error) {
      toast.error('Failed to restock');
    }
  };

  const openRestockDialog = (product) => {
    setRestockProduct(product);
    setRestockForm({
      quantity: '',
      cost_price_per_unit: product.cost_price.toString(),
      expiry_date: '',
      supplier_name: ''
    });
    setShowRestockDialog(true);
  };

  // Sale handlers
  const calculateSaleTotal = () => {
    let subtotal = 0;
    let totalGst = 0;
    
    saleItems.forEach(item => {
      const product = products.find(p => p.id === item.product_id);
      if (product && item.quantity > 0) {
        const itemTotal = product.sell_price * item.quantity;
        const itemGst = itemTotal * (product.gst_rate / 100);
        subtotal += itemTotal;
        totalGst += itemGst;
      }
    });
    
    return { subtotal, totalGst, total: subtotal + totalGst };
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    
    const validItems = saleItems.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const response = await fetch(`${API}/lubricants/sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date: new Date(saleForm.date).toISOString(),
          customer_name: saleForm.customer_name || null,
          customer_mobile: saleForm.customer_mobile || null,
          customer_vehicle: saleForm.customer_vehicle || null,
          items: validItems.map(item => ({ product_id: item.product_id, quantity: parseInt(item.quantity) })),
          payment_method: saleForm.payment_method,
          notes: saleForm.notes || null
        })
      });
      
      if (response.ok) {
        const newSale = await response.json();
        toast.success(`Bill ${newSale.bill_number} generated`);
        setSelectedSale(newSale);
        setShowSaleDialog(false);
        setShowBillDialog(true);
        setSaleItems([{ product_id: '', quantity: 1 }]);
        setSaleForm({
          date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
          customer_name: '', customer_mobile: '', customer_vehicle: '',
          items: [], payment_method: 'cash', notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sale');
      }
    } catch (error) {
      toast.error('Failed to create sale');
    }
  };

  const addSaleItem = () => {
    setSaleItems([...saleItems, { product_id: '', quantity: 1 }]);
  };

  const removeSaleItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const updateSaleItem = (index, field, value) => {
    const updated = [...saleItems];
    updated[index][field] = value;
    setSaleItems(updated);
  };

  const handlePrint = () => {
    // Get the bill content element
    const billContent = document.getElementById('printable-bill');
    if (!billContent) {
      toast.error('Bill content not found');
      return;
    }
    
    // Create invoice number based on bill type
    const invoiceTitle = `LubricantInvoiceNo${selectedSale?.bill_number || ''}`;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    // Write the print content
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${invoiceTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px; 
              max-width: 400px; 
              margin: 0 auto;
              color: #000;
              background: #fff;
            }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .font-mono { font-family: monospace; }
            .text-xs { font-size: 10px; }
            .text-sm { font-size: 12px; }
            .text-lg { font-size: 16px; }
            .text-xl { font-size: 18px; }
            .text-muted-foreground { color: #666; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .items-center { align-items: center; }
            .gap-2 { gap: 8px; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-4 { margin-bottom: 16px; }
            .mt-2 { margin-top: 8px; }
            .mt-3 { margin-top: 12px; }
            .py-2 { padding-top: 8px; padding-bottom: 8px; }
            .py-3 { padding-top: 12px; padding-bottom: 12px; }
            .pt-2 { padding-top: 8px; }
            .pt-3 { padding-top: 12px; }
            .pb-4 { padding-bottom: 16px; }
            .space-y-1 > * + * { margin-top: 4px; }
            .space-y-2 > * + * { margin-top: 8px; }
            .space-y-4 > * + * { margin-top: 16px; }
            .border-b { border-bottom: 1px solid #ccc; }
            .border-t { border-top: 1px solid #ccc; }
            .border-dashed { border-style: dashed; }
            .border-b-2 { border-bottom: 2px solid #ccc; }
            .border-double { border-style: double; }
            .rounded { border-radius: 4px; }
            .bg-gray-100 { background-color: #f3f4f6; }
            .px-2 { padding-left: 8px; padding-right: 8px; }
            .px-3 { padding-left: 12px; padding-right: 12px; }
            .py-1 { padding-top: 4px; padding-bottom: 4px; }
            .bg-green-100 { background-color: #dcfce7; }
            .text-green-700 { color: #15803d; }
            img { max-height: 48px; width: auto; }
            .inline-block { display: inline-block; }
            @media print {
              body { padding: 10px; }
            }
          </style>
        </head>
        <body>
          ${billContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!settings?.enabled) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Droplets className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Lubricants Not Enabled</h2>
          <p className="text-muted-foreground">Please enable Lubricants in Settings to use this feature.</p>
        </div>
      </DashboardLayout>
    );
  }

  const saleTotal = calculateSaleTotal();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Droplets className="w-6 h-6 text-blue-500" />
              Lubricants
            </h1>
            <p className="text-muted-foreground">Manage lubricant inventory and sales</p>
          </div>
          <Button onClick={() => setShowSaleDialog(true)}>
            <ShoppingCart className="w-4 h-4 mr-2" />
            New Sale
          </Button>
        </div>

        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Archive className="w-4 h-4" />
                  <span className="text-sm">Inventory Value</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.inventory?.cost_value || 0).toLocaleString('en-IN')}</p>
                <p className="text-xs text-muted-foreground">{dashboardStats.inventory?.total_items || 0} items</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <IndianRupee className="w-4 h-4" />
                  <span className="text-sm">Today Sales</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.today?.revenue || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Month Revenue</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.month?.revenue || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border-orange-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Month Profit</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.month?.profit || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts */}
        {dashboardStats && (dashboardStats.monthly_trend?.length > 0 || dashboardStats.inventory_aging?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Inventory Aging & Sales Chart */}
            {dashboardStats.inventory_aging?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Stock Value vs Monthly Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={dashboardStats.inventory_aging}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name="Stock Value" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Profit Trend */}
            {dashboardStats.monthly_trend?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Monthly Profit Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={dashboardStats.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Line type="monotone" dataKey="profit" name="Profit" stroke="#22c55e" strokeWidth={2} />
                      <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <Tabs defaultValue="sell" className="space-y-4">
          <TabsList>
            <TabsTrigger value="sell" className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Sales
            </TabsTrigger>
            {isManagerOrAdmin && (
              <TabsTrigger value="inventory" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Inventory
              </TabsTrigger>
            )}
            <TabsTrigger value="ledger" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Ledger
            </TabsTrigger>
          </TabsList>

          {/* Sales Tab */}
          <TabsContent value="sell">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Bill No.</th>
                        <th className="text-left p-3">Customer</th>
                        <th className="text-right p-3">Items</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-right p-3">Profit</th>
                        <th className="text-center p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map(sale => (
                        <tr key={sale.id} className="border-b hover:bg-muted/50">
                          <td className="p-3">{new Date(sale.date).toLocaleDateString('en-IN')}</td>
                          <td className="p-3 font-mono">{sale.bill_number}</td>
                          <td className="p-3">{sale.customer_name || 'Walk-in'}</td>
                          <td className="p-3 text-right">{sale.items?.length || 0}</td>
                          <td className="p-3 text-right text-green-600">₹{sale.total_amount?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right font-medium">₹{sale.profit?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSale(sale);
                                setShowBillDialog(true);
                              }}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {sales.length === 0 && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-muted-foreground">
                            No sales yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inventory Tab */}
          {isManagerOrAdmin && (
            <TabsContent value="inventory">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Product Inventory</CardTitle>
                  <Button onClick={() => { resetProductForm(); setEditingProduct(null); setShowProductDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">Product</th>
                          <th className="text-left p-3">Brand</th>
                          <th className="text-right p-3">Pack Size</th>
                          <th className="text-right p-3">Cost</th>
                          <th className="text-right p-3">Sell Price</th>
                          <th className="text-right p-3">Stock</th>
                          <th className="text-right p-3">Value</th>
                          <th className="text-center p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(product => (
                          <tr key={product.id} className="border-b hover:bg-muted/50">
                            <td className="p-3">
                              <div>{product.name}</div>
                              <div className="text-xs text-muted-foreground">{product.category}</div>
                            </td>
                            <td className="p-3">{product.brand}</td>
                            <td className="p-3 text-right">{product.pack_size_ml}ml</td>
                            <td className="p-3 text-right">₹{product.cost_price}</td>
                            <td className="p-3 text-right">₹{product.sell_price}</td>
                            <td className="p-3 text-right">
                              <span className={product.current_stock <= product.min_stock_alert ? 'text-red-500 font-medium' : ''}>
                                {product.current_stock}
                              </span>
                              {product.current_stock <= product.min_stock_alert && (
                                <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                              )}
                            </td>
                            <td className="p-3 text-right">₹{(product.current_stock * product.cost_price).toLocaleString('en-IN')}</td>
                            <td className="p-3 text-center space-x-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => openRestockDialog(product)}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Restock
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEditProduct(product)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {products.length === 0 && (
                          <tr>
                            <td colSpan={8} className="p-8 text-center text-muted-foreground">
                              No products yet. Add your first product!
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Ledger Tab */}
          <TabsContent value="ledger">
            <Card>
              <CardHeader>
                <CardTitle>Lubricant Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Details</th>
                        <th className="text-right p-3">Debit</th>
                        <th className="text-right p-3">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Combine sales and purchases for ledger view */}
                      {[...sales.map(s => ({ ...s, type: 'sale', sortDate: new Date(s.date) })),
                        ...purchases.map(p => ({ ...p, type: 'purchase', sortDate: new Date(p.date) }))]
                        .sort((a, b) => b.sortDate - a.sortDate)
                        .map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-3">{item.sortDate.toLocaleDateString('en-IN')}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs ${item.type === 'sale' ? 'bg-green-500/20 text-green-600' : 'bg-blue-500/20 text-blue-600'}`}>
                                {item.type === 'sale' ? 'Sale' : 'Purchase'}
                              </span>
                            </td>
                            <td className="p-3">
                              {item.type === 'sale' ? item.bill_number : item.product_name}
                            </td>
                            <td className="p-3 text-right text-red-500">
                              {item.type === 'purchase' ? `₹${item.total_cost?.toLocaleString('en-IN')}` : '-'}
                            </td>
                            <td className="p-3 text-right text-green-600">
                              {item.type === 'sale' ? `₹${item.total_amount?.toLocaleString('en-IN')}` : '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Product Name *</Label>
                <Input
                  required
                  placeholder="e.g., Castrol EDGE 5W-30"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Brand *</Label>
                <Input
                  required
                  placeholder="e.g., Castrol"
                  value={productForm.brand}
                  onChange={(e) => setProductForm({...productForm, brand: e.target.value})}
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select
                  value={productForm.category}
                  onValueChange={(value) => setProductForm({...productForm, category: value})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Engine Oil">Engine Oil</SelectItem>
                    <SelectItem value="Gear Oil">Gear Oil</SelectItem>
                    <SelectItem value="Brake Fluid">Brake Fluid</SelectItem>
                    <SelectItem value="Coolant">Coolant</SelectItem>
                    <SelectItem value="Grease">Grease</SelectItem>
                    <SelectItem value="Transmission Fluid">Transmission Fluid</SelectItem>
                    <SelectItem value="DEF/Urea">DEF/Urea (AdBlue)</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pack Size (ml) *</Label>
                <Input
                  required
                  type="number"
                  placeholder="e.g., 1000"
                  value={productForm.pack_size_ml}
                  onChange={(e) => setProductForm({...productForm, pack_size_ml: e.target.value})}
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  placeholder="Optional"
                  value={productForm.sku}
                  onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                />
              </div>
              <div>
                <Label>Cost Price (₹) *</Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  placeholder="e.g., 350"
                  value={productForm.cost_price}
                  onChange={(e) => setProductForm({...productForm, cost_price: e.target.value})}
                />
              </div>
              <div>
                <Label>Sell Price (₹) *</Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  placeholder="e.g., 450"
                  value={productForm.sell_price}
                  onChange={(e) => setProductForm({...productForm, sell_price: e.target.value})}
                />
              </div>
              <div>
                <Label>GST Rate (%)</Label>
                <Input
                  type="number"
                  value={productForm.gst_rate}
                  onChange={(e) => setProductForm({...productForm, gst_rate: e.target.value})}
                />
              </div>
              <div>
                <Label>Min Stock Alert</Label>
                <Input
                  type="number"
                  value={productForm.min_stock_alert}
                  onChange={(e) => setProductForm({...productForm, min_stock_alert: e.target.value})}
                />
              </div>
              {/* Initial Stock - only show when adding new product */}
              {!editingProduct && (
                <>
                  <div>
                    <Label>Initial Stock Qty *</Label>
                    <Input
                      type="number"
                      required
                      placeholder="e.g., 20"
                      value={productForm.initial_stock}
                      onChange={(e) => setProductForm({...productForm, initial_stock: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={productForm.expiry_date}
                      onChange={(e) => setProductForm({...productForm, expiry_date: e.target.value})}
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>Cancel</Button>
              <Button type="submit">{editingProduct ? 'Update' : 'Add'} Product</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={showRestockDialog} onOpenChange={setShowRestockDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Restock Product</DialogTitle>
          </DialogHeader>
          {restockProduct && (
            <form onSubmit={handleRestockSubmit} className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{restockProduct.name}</p>
                <p className="text-sm text-muted-foreground">{restockProduct.brand} - {restockProduct.pack_size_ml}ml</p>
                <p className="text-sm">Current Stock: <span className="font-medium">{restockProduct.current_stock}</span></p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Quantity to Add *</Label>
                  <Input
                    type="number"
                    required
                    min="1"
                    placeholder="e.g., 20"
                    value={restockForm.quantity}
                    onChange={(e) => setRestockForm({...restockForm, quantity: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Cost/Unit (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={`Default: ₹${restockProduct.cost_price}`}
                    value={restockForm.cost_price_per_unit}
                    onChange={(e) => setRestockForm({...restockForm, cost_price_per_unit: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={restockForm.expiry_date}
                    onChange={(e) => setRestockForm({...restockForm, expiry_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Supplier (Optional)</Label>
                  <Input
                    placeholder="Supplier name"
                    value={restockForm.supplier_name}
                    onChange={(e) => setRestockForm({...restockForm, supplier_name: e.target.value})}
                  />
                </div>
              </div>
              {restockForm.quantity && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm">
                  <p>New Stock: <span className="font-bold">{restockProduct.current_stock + parseInt(restockForm.quantity || 0)}</span></p>
                  <p>Total Cost: <span className="font-bold">₹{((parseFloat(restockForm.cost_price_per_unit) || restockProduct.cost_price) * parseInt(restockForm.quantity || 0)).toLocaleString('en-IN')}</span></p>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRestockDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Stock
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Sale Dialog */}
      <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>New Lubricant Sale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={saleForm.date}
                    onChange={(e) => setSaleForm({...saleForm, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select
                    value={saleForm.payment_method}
                    onValueChange={(value) => setSaleForm({...saleForm, payment_method: value})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Customer Name (Optional)</Label>
                  <Input
                    placeholder="For marketing"
                    value={saleForm.customer_name}
                    onChange={(e) => setSaleForm({...saleForm, customer_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Mobile (Optional)</Label>
                  <Input
                    placeholder="For marketing"
                    value={saleForm.customer_mobile}
                    onChange={(e) => setSaleForm({...saleForm, customer_mobile: e.target.value})}
                  />
                </div>
              </div>

              {/* Sale Items */}
              <div className="space-y-2">
                <Label>Items</Label>
                {saleItems.map((item, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Select
                        value={item.product_id}
                        onValueChange={(value) => updateSaleItem(index, 'product_id', value)}
                      >
                        <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.filter(p => p.current_stock > 0).map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.pack_size_ml}ml) - ₹{p.sell_price} [{p.current_stock} in stock]
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateSaleItem(index, 'quantity', e.target.value)}
                      />
                    </div>
                    {saleItems.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeSaleItem(index)}>
                        <Minus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addSaleItem}>
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </div>
            </div>

            {/* Total - Fixed at bottom */}
            <div className="flex-shrink-0 border-t pt-3 mt-4 space-y-1 bg-background">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>₹{saleTotal.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>GST</span>
                <span>₹{saleTotal.totalGst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-green-600">₹{saleTotal.total.toFixed(2)}</span>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowSaleDialog(false)}>Cancel</Button>
              <Button type="submit">Generate Bill</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col print:max-w-full print:m-0 print:shadow-none">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Lubricant Bill</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            {selectedSale && (
              <BillTemplate
                type="lubricant"
                transaction={selectedSale}
                billSettings={billSettings}
                pumpProfile={pumpProfile}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4 print:hidden">
            <Button variant="outline" onClick={() => setShowBillDialog(false)}>Close</Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Lubricants;
